'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { IconArrowRight, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { GpuCartridge } from './gpu-cartridge'
import { Gauge, Led, Lever, Screw } from './lab-controls'
import { RegionMap } from './region-map'
import { creamPress, labKey, primaryPress } from './lab-styles'
import { useCountUp, useLabPower } from './use-lab-power'

export interface LabRate {
  onDemand: number
  spot: number
  onDemandText: string
  spotText: string
}

export interface LabModule {
  id: string
  /** Two-digit module number, its place in the line-up. */
  code: string
  name: string
  chip: string
  equivalent: string
  vram: number
  vcpus: number
  ram: number
  useCases: string[]
  badge?: string
  /** Base rate × region multiplier, keyed by every region this GPU is offered in. */
  rates: Record<string, LabRate>
  configureHref: string
  /** The configurator pre-set to this GPU and region, per offered region. */
  configureHrefs: Record<string, string>
  detailsHref: string
}

export interface LabRegion {
  id: string
  label: string
  /** Name inside its continent group ("Frankfurt", "US East (Ohio)"). */
  short: string
  group: string
  multiplier: string
}

interface GpuLabProps {
  /** Rentable GPUs, cheapest first. */
  modules: LabModule[]
  regions: LabRegion[]
  groups: string[]
  initialModuleId: string
  initialRegionId: string
  regionCount: number
  currencySymbol: string
  intro: string
  rateNote: string
  photo: { src: string; credit: string }
  /** "Compare all Cloud PCs", rendered on the server. */
  compareAction: ReactNode
}

type Mode = 'on-demand' | 'spot'

export function GpuLab({
  modules,
  regions,
  groups,
  initialModuleId,
  initialRegionId,
  regionCount,
  currencySymbol,
  intro,
  rateNote,
  photo,
  compareAction,
}: GpuLabProps) {
  const [index, setIndex] = useState(() => Math.max(0, modules.findIndex((m) => m.id === initialModuleId)))
  const [regionId, setRegionId] = useState(initialRegionId)
  const [group, setGroup] = useState(() => regions.find((r) => r.id === initialRegionId)?.group ?? groups[0])
  const [mode, setMode] = useState<Mode>('on-demand')
  const [photoOk, setPhotoOk] = useState(true)
  const sectionRef = useRef<HTMLElement>(null)
  const rackRefs = useRef<(HTMLButtonElement | null)[]>([])
  const power = useLabPower(sectionRef)

  const gpu = modules[index] ?? modules[0]
  const region = regions.find((r) => r.id === regionId) ?? regions[0]
  const offered = useMemo(() => new Set(Object.keys(gpu?.rates ?? {})), [gpu])
  const lit = power !== 'armed'

  const rate = gpu?.rates[region?.id ?? ''] ?? null
  const price = rate ? (mode === 'spot' ? rate.spot : rate.onDemand) : null
  const priceText = rate ? (mode === 'spot' ? rate.spotText : rate.onDemandText) : null
  const rolling = useCountUp(price, power === 'on')

  if (!gpu || !region) return null

  const maxVram = Math.max(...modules.map((m) => m.vram))
  const maxCpu = Math.max(...modules.map((m) => m.vcpus))
  const maxRam = Math.max(...modules.map((m) => m.ram))
  const gpusHere = modules.filter((m) => m.rates[region.id]).length
  const groupRegions = regions.filter((r) => r.group === group)
  const rateFor = (m: LabModule, id: string) => {
    const r = m.rates[id]
    return r ? `${mode === 'spot' ? r.spotText : r.onDemandText}/hr` : null
  }

  const select = (next: number, focus = false) => {
    const i = (next + modules.length) % modules.length
    setIndex(i)
    const el = rackRefs.current[i]
    if (focus) el?.focus()
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }

  const onRackKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }
    let next: number | null = null
    if (e.key in moves) next = index + moves[e.key]
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = modules.length - 1
    if (next === null) return
    e.preventDefault()
    select(next, true)
  }

  const pickRegion = (id: string) => {
    setRegionId(id)
    const g = regions.find((r) => r.id === id)?.group
    if (g) setGroup(g)
  }

  const pickGroup = (g: string) => {
    setGroup(g)
    const inGroup = regions.filter((r) => r.group === g)
    if (inGroup.some((r) => r.id === regionId)) return
    const first = inGroup.find((r) => gpu.rates[r.id]) ?? inGroup[0]
    if (first) setRegionId(first.id)
  }

  // The station cartridge leans toward a mouse pointer.
  const onStationMove = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return
    const box = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - box.left) / box.width - 0.5
    const y = (e.clientY - box.top) / box.height - 0.5
    e.currentTarget.style.setProperty('--tilt-x', `${(x * 22).toFixed(1)}deg`)
    e.currentTarget.style.setProperty('--tilt-y', `${(-y * 14).toFixed(1)}deg`)
  }
  const onStationLeave = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.style.removeProperty('--tilt-x')
    e.currentTarget.style.removeProperty('--tilt-y')
  }

  const configureHref = gpu.configureHrefs[region.id] ?? gpu.configureHref
  const shownPrice = rolling === null ? priceText : `${currencySymbol}${rolling.toFixed(2)}`

  return (
    <section
      ref={sectionRef}
      aria-labelledby="catalogue-title"
      data-power={power}
      className="lab-room relative isolate overflow-hidden"
    >
      <div aria-hidden className="lab-texture pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="lab-lightpool pointer-events-none absolute -left-[20%] top-[6%] -z-10 h-[55%] w-[85%] lg:w-[65%]" />

      <div className="mx-auto w-full max-w-[1320px] px-5 py-14 md:px-8 lg:py-20">
        {/* Heading */}
        <div className="lab-power-item flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="lab-chip inline-flex items-center gap-2.5 px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-background">
              <Led on={lit} className="lab-blink" />
              The GPU lab
            </p>
            <h2
              id="catalogue-title"
              className="mt-5 font-heading text-[clamp(2.5rem,5.5vw,4.75rem)] font-bold uppercase leading-[0.9] tracking-[-0.04em] text-background"
            >
              Pick the GPU <span className="lab-glow-text text-accent">for the job.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background/75">{intro}</p>
          </div>
          <div className="shrink-0">{compareAction}</div>
        </div>

        <div className="mt-10 grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <div className="flex min-w-0 flex-col gap-4">
            {/* Docking station */}
            <div
              className="lab-power-item lab-bay relative h-[300px] overflow-hidden sm:h-[380px] lg:h-[430px]"
              style={{ transitionDelay: '80ms' }}
              onPointerMove={onStationMove}
              onPointerLeave={onStationLeave}
            >
              {photoOk && (
                <Image
                  src={photo.src}
                  alt=""
                  fill
                  sizes="(min-width: 1024px) 55vw, 100vw"
                  className="lab-bay-photo object-cover grayscale"
                  onError={() => setPhotoOk(false)}
                />
              )}
              <div aria-hidden className="lab-bay-grid absolute inset-0" />

              <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 p-4">
                <p className="lab-chip inline-flex items-center gap-2 px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-background">
                  <Led on={lit} />
                  Docking station
                </p>
                <div key={`seq-${gpu.id}`} aria-hidden className="flex h-2 w-28 gap-1 sm:w-36">
                  {Array.from({ length: 8 }, (_, i) => (
                    <span
                      key={i}
                      className={cn('lab-cart-led', lit && i < 2 + Math.round((gpu.vcpus / maxCpu) * 6) && 'is-on')}
                      style={lit ? { animationDelay: `${300 + i * 70}ms` } : undefined}
                    />
                  ))}
                </div>
              </div>

              <div aria-hidden className="lab-rail absolute inset-x-[5%] bottom-[22%] h-3" />
              <div aria-hidden className="lab-rail absolute bottom-[22%] left-[5%] h-12 w-3" />
              <div aria-hidden className="lab-rail absolute bottom-[22%] right-[5%] h-12 w-3" />

              <div className="absolute inset-0 flex items-center justify-center pb-[8%]">
                <div key={gpu.id} className="lab-slot-in">
                  <div className="lab-hover">
                    <GpuCartridge
                      size="lg"
                      code={gpu.code}
                      name={gpu.name}
                      vram={gpu.vram}
                      vcpus={gpu.vcpus}
                      rank={index + 1}
                      lit={lit}
                    />
                  </div>
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-4 p-4 sm:p-5">
                <div className="min-w-0">
                  <p className="text-sm text-background/65">
                    Module {gpu.code}
                    {gpu.badge ? ` · ${gpu.badge}` : ''}
                  </p>
                  <p
                    key={`name-${gpu.id}`}
                    className="lab-flicker truncate font-heading text-3xl font-bold uppercase leading-none tracking-tight text-background sm:text-4xl"
                  >
                    {gpu.name}
                  </p>
                </div>
                {photoOk && <p className="shrink-0 text-xs text-background/40">{photo.credit}</p>}
              </div>
            </div>

            {/* Module rack */}
            <div className="lab-power-item lab-shelf relative px-2 pb-6 pt-3 sm:px-3" style={{ transitionDelay: '160ms' }}>
              <div className="mb-2 flex items-center justify-between gap-3 px-1">
                <p className="text-sm font-bold uppercase tracking-[0.12em] text-background">
                  Module rack <span className="font-normal normal-case tracking-normal text-background/60">· {modules.length} GPUs</span>
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    aria-label="Previous GPU"
                    onClick={() => select(index - 1)}
                    className={cn(labKey, 'grid size-10 place-items-center')}
                  >
                    <IconChevronLeft className="size-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label="Next GPU"
                    onClick={() => select(index + 1)}
                    className={cn(labKey, 'grid size-10 place-items-center')}
                  >
                    <IconChevronRight className="size-5" aria-hidden />
                  </button>
                </div>
              </div>

              <div
                role="radiogroup"
                aria-label="GPU"
                onKeyDown={onRackKey}
                className="-mx-2 flex snap-x snap-mandatory gap-2 overflow-x-auto px-2 pb-1 [scrollbar-width:none] sm:-mx-3 sm:px-3 lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0"
              >
                {modules.map((m, i) => {
                  const selected = i === index
                  const mRate = rateFor(m, region.id)
                  return (
                    <div
                      key={m.id}
                      className="lab-power-item w-[10rem] shrink-0 snap-start lg:w-auto"
                      style={{ transitionDelay: `${220 + i * 90}ms` }}
                    >
                      <button
                        ref={(el) => {
                          rackRefs.current[i] = el
                        }}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => select(i)}
                        className={cn(
                          'group relative flex h-full w-full flex-col items-center gap-2 px-2 pb-3 pt-6 text-center outline-none transition-[background-color,box-shadow] duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent active:translate-y-[2px]',
                          selected
                            ? 'bg-background/10 shadow-[inset_0_-3px_0_0_var(--accent)]'
                            : 'hover:bg-background/5'
                        )}
                      >
                        <Led on={selected && lit} className="absolute left-2 top-2" />
                        <span className="absolute right-2 top-1.5 text-xs font-bold tabular-nums text-background/45">{m.code}</span>
                        <GpuCartridge
                          code={m.code}
                          name={m.name}
                          vram={m.vram}
                          vcpus={m.vcpus}
                          rank={i + 1}
                          lit={selected && lit}
                          className={cn(
                            'mb-2 transition-[translate,scale,opacity] duration-500',
                            selected ? '-translate-y-1 scale-105' : 'opacity-70 group-hover:-translate-y-1 group-hover:opacity-100'
                          )}
                        />
                        <span className="font-heading text-base font-bold uppercase leading-tight text-background">{m.name}</span>
                        <span className="text-sm text-background/60">
                          {m.vram} GB · {m.vcpus} vCPU
                        </span>
                        <span className={cn('text-sm font-bold tabular-nums', mRate ? 'text-accent' : 'text-background/40')}>
                          {mRate ?? 'Not offered here'}
                        </span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Instrument panel */}
          <div className="lab-power-item lab-plate relative flex flex-col gap-3 p-4 sm:p-5" style={{ transitionDelay: '140ms' }}>
            <Screw className="left-2 top-2" angle={30} />
            <Screw className="right-2 top-2" angle={120} />
            <Screw className="bottom-2 left-2" angle={80} />
            <Screw className="bottom-2 right-2" angle={160} />

            <div className="flex items-center justify-between gap-3 px-1 pt-1">
              <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-background">
                <Led on={lit} className={lit ? 'lab-blink' : undefined} />
                Instrument panel
              </p>
              {gpu.badge && (
                <span className="bg-primary px-2 py-1 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground">
                  {gpu.badge}
                </span>
              )}
            </div>

            <p className="sr-only" aria-live="polite">
              {gpu.name} in {region.label}: {priceText ? `${priceText} per hour, ${mode}` : 'not offered'}
            </p>

            {/* Rate display + billing lever */}
            <div className="lab-well flex flex-col items-stretch gap-3 p-3 sm:flex-row sm:items-center">
              <div className="lab-lcd min-w-0 flex-1 px-4 py-3" aria-hidden>
                <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[0.1em]">
                  <span className="truncate">
                    {gpu.chip} · {gpu.vram} GB
                  </span>
                  <span className="shrink-0 opacity-80">{lit ? (mode === 'spot' ? 'Spot' : 'On-demand') : 'Standby'}</span>
                </div>
                <p
                  key={`${gpu.id}-${region.id}-${mode}`}
                  className="lab-flicker lab-glow-text mt-2 whitespace-nowrap font-bold leading-none tabular-nums"
                >
                  {shownPrice ? (
                    <>
                      <span className="text-[2.75rem] sm:text-5xl">{shownPrice}</span>
                      <span className="ml-1 text-lg">/hr</span>
                    </>
                  ) : (
                    <span className="text-3xl">Not offered</span>
                  )}
                </p>
                <p className="mt-2 truncate text-xs leading-snug opacity-80">
                  {rate ? `${region.label} · base × ${region.multiplier}` : `Not in ${region.label}`}
                </p>
              </div>
              <Lever
                label="Billing"
                on={mode === 'spot'}
                offLabel="On-demand"
                onLabel="Spot"
                onToggle={() => setMode((m) => (m === 'spot' ? 'on-demand' : 'spot'))}
              />
            </div>

            {/* Gauges */}
            <div className="lab-well grid grid-cols-4 gap-1 p-2 sm:p-3">
              <Gauge label="VRAM" value={gpu.vram} max={maxVram} unit="GB" on={lit} />
              <Gauge label="vCPU" value={gpu.vcpus} max={maxCpu} on={lit} />
              <Gauge label="RAM" value={gpu.ram} max={maxRam} unit="GB" on={lit} />
              <Gauge label="Regions" value={offered.size} max={regionCount} on={lit} />
            </div>

            {/* Workloads */}
            <div className="lab-well p-3 sm:p-4">
              <p className="text-sm font-bold uppercase tracking-[0.12em] text-background">Suited to</p>
              <ul className="mt-2.5 flex flex-wrap gap-2">
                {gpu.useCases.map((useCase) => (
                  <li key={useCase} className="lab-chip flex items-center gap-2 px-3 py-1.5 text-sm text-background">
                    <Led on={lit} />
                    {useCase}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm text-background/65">{gpu.equivalent}</p>
            </div>

            {/* Availability */}
            <div className="lab-well flex items-start gap-3 p-3 sm:p-4">
              <Led on={lit && !!rate} className={cn('mt-1.5 size-3', lit && rate && 'lab-blink')} />
              <div className="min-w-0">
                <p className="font-bold text-background">
                  {rate ? `Offered in ${region.label}` : `Not offered in ${region.label}`}
                </p>
                <p className="text-sm text-background/65">
                  Runs in {offered.size} of {regionCount} regions
                  {rate ? '.' : '. Pick a lit pin on the map below.'}
                </p>
              </div>
            </div>

            <div className="mt-auto flex flex-col gap-3 pt-2 sm:flex-row lg:flex-col xl:flex-row">
              <Button className={cn(primaryPress, 'flex-1')} asChild>
                <Link href={configureHref}>
                  Configure GPU
                  <IconArrowRight className="size-4" aria-hidden />
                </Link>
              </Button>
              <Button className={cn(creamPress, 'flex-1')} asChild>
                <Link href={gpu.detailsHref}>View Details</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Region selector */}
        <div className="lab-power-item lab-plate relative mt-4 p-4 sm:p-5" style={{ transitionDelay: '200ms' }}>
          <Screw className="left-2 top-2" angle={60} />
          <Screw className="right-2 top-2" angle={10} />
          <Screw className="bottom-2 left-2" angle={140} />
          <Screw className="bottom-2 right-2" angle={95} />

          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-1 pb-3">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-background">
              <Led on={lit} className={lit ? 'lab-blink' : undefined} />
              Region selector
            </p>
            <p className="text-sm text-background/60">
              {regionCount} regions · lit pins run the {gpu.name}
            </p>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <div className="lab-well p-2 sm:p-3">
              <RegionMap
                regions={regions}
                offered={offered}
                selectedId={region.id}
                on={lit}
                onSelect={pickRegion}
              />
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-1 pt-3">
                <p className="font-heading text-xl font-bold uppercase tracking-tight text-background">{region.label}</p>
                <p className="text-sm text-background/65">
                  Multiplier × {region.multiplier} · {gpusHere} of {modules.length} GPUs here
                </p>
              </div>
            </div>

            <div className="lab-well flex flex-col gap-3 p-3 sm:p-4">
              <div role="group" aria-label="Continent" className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
                {groups.map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={g === group}
                    onClick={() => pickGroup(g)}
                    className={cn(labKey, 'flex min-h-14 flex-col items-start justify-between gap-1 px-3 py-2 text-left')}
                  >
                    <span className="text-sm font-bold uppercase leading-tight tracking-[0.04em]">{g}</span>
                    <span className="text-xs opacity-75">{regions.filter((r) => r.group === g).length} regions</span>
                  </button>
                ))}
              </div>

              <div role="group" aria-label={`Regions in ${group}`} className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {groupRegions.map((r) => {
                  const rRate = rateFor(gpu, r.id)
                  const selected = r.id === region.id
                  return (
                    <button
                      key={r.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => pickRegion(r.id)}
                      className={cn(labKey, 'flex min-h-16 flex-col items-start justify-between gap-1 px-3 py-2 text-left')}
                    >
                      <span className="flex w-full items-start justify-between gap-2">
                        <span className="text-sm font-bold leading-tight">{r.short}</span>
                        {!selected && <Led on={lit && !!rRate} className="mt-1" />}
                      </span>
                      <span className="text-sm tabular-nums opacity-80">{rRate ?? 'Not offered'}</span>
                    </button>
                  )
                })}
              </div>

              <p className="mt-auto text-xs leading-relaxed text-background/55">{rateNote}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
